/**
 * Parsers for MCP-originated audit rows.
 *
 * The past-experience MCP servers (Claude.ai / cowork traffic) write their
 * audit trail via shared/mcp_conversation_audit.py: each tool call becomes an
 * ACTIONS row whose action_output carries the queries the server received and
 * its per-query fan-out detail, a LLM_REQUESTS row per fanned-out LLM call, and
 * a TURN_OUTPUTS row whose output_content carries counts only. These helpers
 * turn those loosely-typed JSON blobs into the shapes the admin components
 * render. Kept free of React so the logic is unit-testable in node.
 */

export interface McpQuery {
  query: string;
  targetColumns: string[];
}

export interface McpDealSearchData {
  goal: string | null;
  queries: McpQuery[];
  dealCount: number | null;
  documentCount: number | null;
}

export interface McpFanOutPath {
  /** The path's place in the query array the caller sent, 0-based. */
  position: number;
  query: string;
  /** The LLM_Requests ids this path produced, as named on the action row. */
  llmRequestIds: string[];
}

export interface McpLlmRequestGroup<T> {
  /** The query that produced these requests, or null when the action named none. */
  query: string | null;
  position: number;
  requests: T[];
}

export interface McpDocQaData {
  queries: McpQuery[];
  chunkCount: number | null;
  queryCount: number | null;
  datasetCount: number | null;
  distilled: boolean;
  error: string | null;
}

/** Coerce an action_output / output_content value (object or JSON string) to a record. */
function asRecord(value: unknown): Record<string, unknown> | null {
  let candidate = value;
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return null;
    }
  }
  if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
    return candidate as Record<string, unknown>;
  }
  return null;
}

/** Read a numeric field off an audit blob, or null when it is absent/not a number. */
function toCount(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

/** The user's one-line goal (conversation_context) stored on the audit row, if any. */
export function extractMcpGoal(actionOutput: unknown): string | null {
  const record = asRecord(actionOutput);
  const goal = record?.conversation_context;
  return typeof goal === "string" && goal.trim() ? goal : null;
}

function toMcpQuery(item: unknown): McpQuery | null {
  // deals-search stores {query, target_columns?}; doc-q-a stores plain strings.
  if (typeof item === "string") {
    return item.trim() ? { query: item, targetColumns: [] } : null;
  }
  const record = asRecord(item);
  const text = record?.query;
  if (typeof text !== "string" || !text.trim()) return null;
  const rawColumns = record?.target_columns;
  const targetColumns = Array.isArray(rawColumns)
    ? rawColumns.filter((c): c is string => typeof c === "string")
    : [];
  return { query: text, targetColumns };
}

/** The array of queries an MCP search action received, or [] when absent. */
export function extractMcpQueries(actionOutput: unknown): McpQuery[] {
  const record = asRecord(actionOutput);
  const raw = record?.queries;
  if (!Array.isArray(raw)) return [];
  return raw.map(toMcpQuery).filter((q): q is McpQuery => q !== null);
}

/**
 * The per-query fan-out paths recorded on an MCP action row, in request order.
 *
 * Both servers sort their fan-out by the caller's query position before writing
 * it, so the stored array order is the order the caller asked in — nothing here
 * needs to re-sort it.
 */
export function parseMcpFanOut(actionOutput: unknown): McpFanOutPath[] {
  const record = asRecord(actionOutput);
  const raw = record?.fan_out;
  if (!Array.isArray(raw)) return [];
  const paths: McpFanOutPath[] = [];
  raw.forEach((item) => {
    const entry = asRecord(item);
    const query = entry?.query;
    if (typeof query !== "string" || !query.trim()) return;
    const ids = entry?.llm_request_ids;
    paths.push({
      position: paths.length,
      query,
      llmRequestIds: Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [],
    });
  });
  return paths;
}

/**
 * Group an action's LLM_Requests rows under the query that produced them.
 *
 * The rows themselves carry no query, and every row of an insert batch shares
 * one CREATED_AT, so the action row's id lists are the only thing that can
 * attribute them. Paths that produced no LLM call are dropped (a doc-q-a
 * passthrough makes none), and rows no path claims — or every row, for an
 * action written before the ids were recorded — fall into a trailing unnamed
 * group so nothing is hidden.
 */
export function groupMcpLlmRequestsByQuery<T extends { llm_request_id: string }>(
  requests: T[],
  paths: McpFanOutPath[],
): McpLlmRequestGroup<T>[] {
  const owner = new Map<string, number>();
  paths.forEach((path) => {
    path.llmRequestIds.forEach((id) => owner.set(id, path.position));
  });
  const byPosition = new Map<number, T[]>();
  const unclaimed: T[] = [];
  requests.forEach((request) => {
    const position = owner.get(request.llm_request_id);
    if (position === undefined) {
      unclaimed.push(request);
      return;
    }
    const bucket = byPosition.get(position);
    if (bucket) bucket.push(request);
    else byPosition.set(position, [request]);
  });
  // Built from the buckets rather than filtered-then-looked-up: a path that
  // claimed no rows simply has no bucket, so a group always holds the requests
  // it was created from instead of a lookup the types cannot see is safe.
  const groups: McpLlmRequestGroup<T>[] = [];
  paths.forEach((path) => {
    const bucket = byPosition.get(path.position);
    if (bucket) {
      groups.push({ query: path.query, position: path.position, requests: bucket });
    }
  });
  if (unclaimed.length) {
    groups.push({ query: null, position: paths.length, requests: unclaimed });
  }
  return groups;
}

/** Parse a Turn_Outputs row written by the deals-search MCP server, else null. */
export function parseMcpDealSearchOutput(
  outputType: string,
  outputContent: unknown,
): McpDealSearchData | null {
  if (outputType !== "mcp_deal_search") return null;
  const record = asRecord(outputContent);
  if (!record) return null;
  // Counts only: which deals came back is on the action row's fan-out detail and
  // the per-deal LLM_REQUESTS rows, so the output row no longer repeats them.
  return {
    goal: extractMcpGoal(record),
    queries: extractMcpQueries(record),
    dealCount: toCount(record.deal_count),
    documentCount: toCount(record.document_count),
  };
}

/** Parse a Turn_Outputs row written by the doc-q-a MCP server, else null. */
export function parseMcpDocQaOutput(
  outputType: string,
  outputContent: unknown,
): McpDocQaData | null {
  if (outputType !== "mcp_doc_qa_search") return null;
  const record = asRecord(outputContent);
  if (!record) return null;
  // Counts only, same as deal search: each query's distilled answer is stored
  // untruncated on its synthesis LLM_Requests row, and the per-chunk relevance
  // verdicts are on the action row's fan-out detail.
  return {
    queries: extractMcpQueries(record),
    chunkCount: toCount(record.chunk_count),
    queryCount: toCount(record.query_count),
    datasetCount: toCount(record.dataset_count),
    distilled: record.distilled === true,
    error: typeof record.error === "string" && record.error.trim() ? record.error : null,
  };
}
